import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { config } from './config';
import { openAppDb } from './db/schema';
import { createRuntime } from './runtime';
import { FakeAuthProvider } from './auth/fake';
import type { AuthProvider } from './auth/provider';
import { App } from './ui/App';
import './styles.css';

/**
 * Boot.
 *
 * The order matters in one place: IndexedDB opens before the first render, so
 * the first paint is already the mirror rather than an empty screen that fills
 * in. On an installed PWA that is the difference between opening the book and
 * watching it load.
 *
 * The service worker precaches the shell and **nothing else**. There is no
 * runtime caching of `/v1` and there must not be: the app reads its own mirror,
 * and an HTTP cache in front of `core-api` would be a *second* offline store
 * with its own staleness that the status strip could not see and §8.1's "the UI
 * says plainly how stale it is" could not describe. One counter, one book — and
 * one cache.
 */

async function boot(): Promise<void> {
  // Firebase is imported dynamically: it is the largest thing in the bundle by
  // some distance, and a development build never touches it. One chunk that
  // only the real sign-in pays for.
  const auth: AuthProvider =
    config.auth.mode === 'fake'
      ? new FakeAuthProvider({ tenantId: config.auth.tenantId })
      : new (await import('./auth/firebase')).FirebaseAuthProvider(config.auth.firebase);

  const db = await openAppDb();
  const runtime = createRuntime({
    db,
    auth,
    baseUrl: config.apiBaseUrl,
    pastDays: config.mirrorPastDays,
    futureDays: config.mirrorFutureDays,
    chunkDays: config.calendarChunkDays,
  });

  const container = document.getElementById('root');
  if (container === null) throw new Error('no #root');
  createRoot(container).render(
    <StrictMode>
      <App runtime={runtime} />
    </StrictMode>,
  );

  registerServiceWorker();
}

/**
 * Register the generated worker, by hand.
 *
 * It is registered **after** the first render, not before: a service worker
 * install competing with the first paint is a slower start for no benefit, and
 * the app is fully usable — offline included — before the worker exists at all,
 * because the mirror is IndexedDB rather than an HTTP cache.
 *
 * `registerType: 'autoUpdate'` plus `skipWaiting` means a new build takes over
 * on the next load. On a counter iPad that is left open for weeks, waiting for
 * every tab to close would mean shipping a fix nobody receives.
 */
function registerServiceWorker(): void {
  if (import.meta.env.DEV) return;
  if (!('serviceWorker' in navigator)) return;
  navigator.serviceWorker.register('/sw.js').catch(() => {
    // No worker means no offline *shell*. The book still works for as long as
    // the tab lives, so this is a degradation, not a failure to start.
  });
}

void boot();
